// Catalog host for the federated multi-agent chat widget (smart_ai_agent).
// Loads the remote lazily, passes the signed-in user and the part on screen,
// and fails closed: if the remote cannot load, the catalog keeps working.

import React, { Suspense, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@ciea.ai/smart-auth-lib';
import { useAssistantContextStore } from '../../stores/assistantContextStore';

const MultiAgentChatWidget = React.lazy(() => import('smart_ai_agent/MultiAgentChatWidget'));

class AssistantErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error('[CatalogAiAssistant] Assistant failed to load; hiding it.', error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const CatalogAiAssistant: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const part = useAssistantContextStore((s) => s.part);

  const userContext = useMemo(() => {
    if (!isAuthenticated || !user) return undefined;
    const u = user as Record<string, any>;
    return {
      userId: u.id || u._id || '',
      email: u.email,
      name: u.name || u.displayName || u.fullName,
      tenantId: u.tenantId,
      organizationId: u.organizationId || u.orgId,
      roles: u.roles || u.permissions || [],
    };
  }, [isAuthenticated, user]);

  // Recreated when the part or route changes so the widget re-renders its
  // header and suggestions for what is on screen.
  const getPageContext = useCallback(
    () => ({
      appId: 'smart-catalog',
      route: location.pathname,
      ...(part ? { entity: { type: 'part' as const, id: part.id, partNumber: part.partNumber, mpn: part.mpn } } : {}),
    }),
    [location.pathname, part],
  );

  if (!userContext) return null;

  return (
    <AssistantErrorBoundary>
      <Suspense fallback={null}>
        <MultiAgentChatWidget appProfile="smart-catalog" userContext={userContext} getPageContext={getPageContext} />
      </Suspense>
    </AssistantErrorBoundary>
  );
};

export default CatalogAiAssistant;
